<template>
    <div class="weeduc-admin-page corporate-page corporate-preferences-page text-start p-0">
        <header class="weeduc-admin-page__header">
            <div class="weeduc-admin-page__header-lead">
                <TitlePage
                    :title="t.preferences.title"
                    :subtitle="t.preferences.subtitle"
                    :hasMargin="false"
                />
            </div>
        </header>

        <div class="weeduc-admin-page__shell">
            <div v-if="!canEdit" class="corporate-preferences-banner">
                {{ t.preferences.readOnly }}
            </div>

            <div class="weeduc-admin-page__panel">
                <div v-if="loading" class="corporate-placeholder">
                    <Loader :isActive="false" />
                </div>
                <div v-else-if="error" class="corporate-placeholder">{{ t.common.error }}</div>

                <template v-else>
                    <nav class="weeduc-admin-tabs" :aria-label="t.preferences.tabsAriaLabel">
                        <button
                            v-for="tab in tabs"
                            :key="tab.id"
                            type="button"
                            class="weeduc-admin-tab"
                            :class="{ 'weeduc-admin-tab--active': activeTab === tab.id }"
                            @click="activeTab = tab.id"
                        >
                            {{ tab.label }}
                        </button>
                    </nav>

                    <!-- Site público -->
                    <section v-show="activeTab === 'site'" class="corporate-preferences-panel">
                        <p class="corporate-section-label">{{ t.preferences.sectionVisibility }}</p>
                        <p class="corporate-preferences-lead">{{ t.preferences.portalLead }}</p>

                        <div class="corporate-preferences-list">
                            <div class="corporate-preferences-row">
                                <div>
                                    <strong>{{ t.preferences.siteEnabled }}</strong>
                                    <p>{{ t.preferences.siteEnabledHint }}</p>
                                </div>
                                <v-switch
                                    :model-value="form.siteEnabled"
                                    color="primary"
                                    hide-details
                                    inset
                                    :disabled="!canEdit || saving"
                                    :loading="savingKey === 'siteEnabled'"
                                    @update:model-value="(val) => onToggle('siteEnabled', val)"
                                />
                            </div>
                        </div>

                        <template v-if="form.siteEnabled">
                            <p class="corporate-section-label">{{ t.preferences.sectionUrls }}</p>
                            <p class="corporate-preferences-lead">{{ t.preferences.urlsLead }}</p>

                            <div class="corporate-preferences-urls">
                                <article
                                    v-for="item in siteUrlItems"
                                    :key="item.key"
                                    class="corporate-preferences-url-card"
                                    :class="{
                                        'corporate-preferences-url-card--main': item.key === 'url',
                                        'is-saving': savingKey === item.key,
                                    }"
                                >
                                    <div class="corporate-preferences-url-card__copy">
                                        <strong>{{ item.title }}</strong>
                                        <p>{{ item.hint }}</p>
                                    </div>
                                    <div class="corporate-preferences-url-card__control">
                                        <input
                                            :id="`corporate-url-${item.key}`"
                                            v-model="form[item.key]"
                                            type="text"
                                            class="corporate-preferences-url-card__input"
                                            :placeholder="t.preferences.urlPlaceholder"
                                            :disabled="!canEdit || saving"
                                            :aria-label="item.title"
                                            @blur="onUrlBlur(item.key)"
                                        />
                                        <div class="corporate-preferences-url-card__actions">
                                            <button
                                                type="button"
                                                class="corporate-preferences-url-action"
                                                :disabled="!canOpenUrl(form[item.key])"
                                                :aria-label="t.preferences.openUrl"
                                                @mousedown.prevent
                                                @click="openUrl(form[item.key])"
                                            >
                                                <v-icon size="18">mdi-open-in-new</v-icon>
                                            </button>
                                            <button
                                                type="button"
                                                class="corporate-preferences-url-action"
                                                :disabled="!canOpenUrl(form[item.key])"
                                                :aria-label="t.preferences.copyUrl"
                                                @mousedown.prevent
                                                @click="copyUrl(form[item.key])"
                                            >
                                                <v-icon size="18">mdi-content-copy</v-icon>
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            </div>
                        </template>
                    </section>

                    <!-- Portal do colaborador -->
                    <section
                        v-show="activeTab === 'collaborator'"
                        class="corporate-preferences-panel"
                    >
                        <p class="corporate-section-label">{{ t.preferences.sectionModules }}</p>
                        <p class="corporate-preferences-lead">{{ t.preferences.collaboratorLead }}</p>

                        <div class="corporate-preferences-list">
                            <div
                                v-for="item in collaboratorToggleItems"
                                :key="item.key"
                                class="corporate-preferences-row"
                            >
                                <div>
                                    <strong>{{ item.title }}</strong>
                                    <p>{{ item.hint }}</p>
                                </div>
                                <v-switch
                                    :model-value="form[item.key]"
                                    color="primary"
                                    hide-details
                                    inset
                                    :disabled="!canEdit || saving"
                                    :loading="savingKey === item.key"
                                    @update:model-value="(val) => onToggle(item.key, val)"
                                />
                            </div>
                        </div>
                    </section>

                    <!-- Layout do portal -->
                    <section
                        v-show="activeTab === 'banner'"
                        class="corporate-preferences-panel"
                    >
                        <p class="corporate-section-label">{{ t.preferences.logoLabel }}</p>
                        <p class="corporate-preferences-lead">{{ t.preferences.logoHint }}</p>
                        <div class="corporate-preferences-upload">
                            <CustomFileInput
                                v-model="logoValue"
                                height="120"
                                :can-delete="false"
                                :info-text="t.preferences.uploadClick"
                                @update:modelValue="onLogoChange"
                            />
                        </div>
                    </section>

                    <!-- Domínio -->
                    <section v-show="activeTab === 'domain'" class="corporate-preferences-panel">
                        <div v-if="domainRefreshing" class="corporate-placeholder">
                            <Loader :isActive="false" />
                        </div>
                        <div v-else-if="domainError" class="corporate-placeholder">
                            <p>{{ t.preferences.domainLoadError }}</p>
                            <button
                                type="button"
                                class="corporate-preferences-domain-retry"
                                @click="loadDomain"
                            >
                                {{ t.preferences.domainRetry }}
                            </button>
                        </div>

                        <template v-else>
                            <p class="corporate-section-label">{{ t.preferences.domainWeeducTitle }}</p>
                            <p class="corporate-preferences-lead">{{ t.preferences.domainWeeducLead }}</p>
                            <p class="corporate-preferences-callout">{{ t.preferences.domainUpdateWarning }}</p>

                            <div class="corporate-preferences-urls">
                                <article
                                    class="corporate-preferences-url-card corporate-preferences-url-card--main"
                                    :class="{ 'is-saving': domainSaving || domainVerifying }"
                                >
                                    <div class="corporate-preferences-url-card__copy">
                                        <strong>{{ t.preferences.domainName }}</strong>
                                        <p>{{ t.preferences.domainNameHint }}</p>
                                    </div>
                                    <div class="corporate-preferences-url-card__control">
                                        <input
                                            id="corporate-domain-slug"
                                            v-model="domain.slug"
                                            type="text"
                                            class="corporate-preferences-url-card__input"
                                            :placeholder="t.preferences.domainNamePlaceholder"
                                            :disabled="!canEdit || domainSaving"
                                            :aria-label="t.preferences.domainName"
                                            maxlength="30"
                                            autocomplete="off"
                                            @input="onDomainSlugInput"
                                        />
                                        <v-icon
                                            v-if="hasValidDomain === true"
                                            class="corporate-preferences-domain-status"
                                            color="success"
                                            size="20"
                                            :aria-label="t.preferences.domainAvailable"
                                        >
                                            mdi-check-circle
                                        </v-icon>
                                        <v-icon
                                            v-else-if="hasValidDomain === false"
                                            class="corporate-preferences-domain-status"
                                            color="error"
                                            size="20"
                                            :aria-label="t.preferences.domainUnavailable"
                                        >
                                            mdi-close-circle
                                        </v-icon>
                                        <button
                                            type="button"
                                            class="corporate-preferences-domain-verify"
                                            :disabled="!canEdit || domainSaving || domainVerifying || !domain.url"
                                            @click="validateDomain"
                                        >
                                            {{ t.preferences.domainVerify }}
                                        </button>
                                    </div>
                                </article>

                                <article class="corporate-preferences-url-card corporate-preferences-url-card--main">
                                    <div class="corporate-preferences-url-card__copy">
                                        <strong>{{ t.preferences.domainFinal }}</strong>
                                        <p>{{ t.preferences.domainFinalHint }}</p>
                                    </div>
                                    <div class="corporate-preferences-url-card__control">
                                        <input
                                            id="corporate-domain-url"
                                            :value="domain.url"
                                            type="text"
                                            class="corporate-preferences-url-card__input"
                                            :placeholder="t.preferences.domainFinalPlaceholder"
                                            disabled
                                            :aria-label="t.preferences.domainFinal"
                                        />
                                        <div class="corporate-preferences-url-card__actions">
                                            <button
                                                type="button"
                                                class="corporate-preferences-url-action"
                                                :disabled="!canOpenUrl(domain.url)"
                                                :aria-label="t.preferences.openUrl"
                                                @click="openUrl(domain.url)"
                                            >
                                                <v-icon size="18">mdi-open-in-new</v-icon>
                                            </button>
                                            <button
                                                type="button"
                                                class="corporate-preferences-url-action"
                                                :disabled="!canOpenUrl(domain.url)"
                                                :aria-label="t.preferences.copyUrl"
                                                @click="copyUrl(domain.url)"
                                            >
                                                <v-icon size="18">mdi-content-copy</v-icon>
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            </div>

                            <p class="corporate-section-label">{{ t.preferences.domainOwnTitle }}</p>
                            <p class="corporate-preferences-lead">{{ t.preferences.domainOwnLead }}</p>
                            <p class="corporate-preferences-callout">{{ t.preferences.domainOwnWarning }}</p>

                            <div class="corporate-preferences-urls">
                                <article
                                    v-for="item in domainOwnItems"
                                    :key="item.key"
                                    class="corporate-preferences-url-card"
                                    :class="{ 'is-saving': domainSaving }"
                                >
                                    <div class="corporate-preferences-url-card__copy">
                                        <strong>{{ item.title }}</strong>
                                        <p>{{ item.hint }}</p>
                                    </div>
                                    <div class="corporate-preferences-url-card__control">
                                        <input
                                            :id="`corporate-domain-${item.key}`"
                                            v-model="domain[item.key]"
                                            type="text"
                                            class="corporate-preferences-url-card__input"
                                            :placeholder="t.preferences.urlPlaceholder"
                                            :disabled="!canEdit || domainSaving"
                                            :aria-label="item.title"
                                            autocomplete="off"
                                        />
                                        <div class="corporate-preferences-url-card__actions">
                                            <button
                                                type="button"
                                                class="corporate-preferences-url-action"
                                                :disabled="!canOpenUrl(domain[item.key])"
                                                :aria-label="t.preferences.openUrl"
                                                @click="openUrl(domain[item.key])"
                                            >
                                                <v-icon size="18">mdi-open-in-new</v-icon>
                                            </button>
                                            <button
                                                type="button"
                                                class="corporate-preferences-url-action"
                                                :disabled="!canOpenUrl(domain[item.key])"
                                                :aria-label="t.preferences.copyUrl"
                                                @click="copyUrl(domain[item.key])"
                                            >
                                                <v-icon size="18">mdi-content-copy</v-icon>
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            </div>

                            <p class="corporate-section-label">{{ t.preferences.domainDnsTitle }}</p>
                            <p class="corporate-preferences-lead">{{ t.preferences.domainDnsLead }}</p>

                            <div class="corporate-preferences-urls">
                                <article class="corporate-preferences-url-card corporate-preferences-url-card--main">
                                    <div class="corporate-preferences-url-card__copy">
                                        <span class="corporate-preferences-dns-badge">CNAME</span>
                                        <strong>{{ t.preferences.domainCname }}</strong>
                                        <p>{{ t.preferences.domainCnameHint }}</p>
                                    </div>
                                    <div class="corporate-preferences-url-card__control">
                                        <input
                                            :value="dnsCnameTarget"
                                            type="text"
                                            class="corporate-preferences-url-card__input"
                                            disabled
                                            readonly
                                            :aria-label="t.preferences.domainCname"
                                        />
                                        <div class="corporate-preferences-url-card__actions">
                                            <button
                                                type="button"
                                                class="corporate-preferences-url-action"
                                                :aria-label="t.preferences.copyUrl"
                                                @click="copyRaw(dnsCnameTarget)"
                                            >
                                                <v-icon size="18">mdi-content-copy</v-icon>
                                            </button>
                                        </div>
                                    </div>
                                </article>

                                <article class="corporate-preferences-url-card corporate-preferences-url-card--main">
                                    <div class="corporate-preferences-url-card__copy">
                                        <span class="corporate-preferences-dns-badge">A</span>
                                        <strong>{{ t.preferences.domainARecord }}</strong>
                                        <p>{{ t.preferences.domainARecordHint }}</p>
                                    </div>
                                    <div class="corporate-preferences-url-card__control corporate-preferences-url-card__control--stack">
                                        <div
                                            v-for="ip in dnsARecords"
                                            :key="ip.key"
                                            class="corporate-preferences-url-card__control"
                                        >
                                            <input
                                                :value="ip.value"
                                                type="text"
                                                class="corporate-preferences-url-card__input"
                                                :placeholder="t.preferences.domainIpEmpty"
                                                disabled
                                                readonly
                                                :aria-label="ip.label"
                                            />
                                            <div class="corporate-preferences-url-card__actions">
                                                <button
                                                    type="button"
                                                    class="corporate-preferences-url-action"
                                                    :disabled="!ip.value"
                                                    :aria-label="t.preferences.copyUrl"
                                                    @click="copyRaw(ip.value)"
                                                >
                                                    <v-icon size="18">mdi-content-copy</v-icon>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            </div>

                            <div class="corporate-preferences-domain-actions">
                                <v-btn
                                    color="primary"
                                    variant="flat"
                                    rounded="lg"
                                    class="text-none"
                                    :disabled="!canEdit || domainSaving"
                                    :loading="domainSaving"
                                    @click="saveDomain"
                                >
                                    {{ t.common.save }}
                                </v-btn>
                            </div>
                        </template>
                    </section>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { toast } from 'vue3-toastify';
import TitlePage from '@/components/Weflix/titlePage.vue';
import Loader from '@/components/Loader/loader.vue';
import CustomFileInput from '@/components/Form/CustomFileInput.vue';
import { corporateI18n } from '@/i18n/mixins';
import websiteApi from '@/utils/Weflix/website';
import apiSiteDomain from '@/utils/Site/domain';
import apiAWS from '@/utils/storage/aws';
import { canConfigureCorporate } from '@/utils/corporate/permissions';

const DNS_CNAME_TARGET = 'sni.weeduc.tv';

const emptyDomain = () => ({
    slug: '',
    url: '',
    url1: '',
    url2: '',
});

const emptyForm = () => ({
    siteEnabled: true,
    showCertificate: true,
    showInquiry: false,
    showForum: false,
    showWarning: false,
    showNotification: true,
    url: '',
    url1: '',
    url2: '',
});

function normalizeUrlField(value) {
    if (value === undefined || value === null) return '';
    return String(value).trim();
}

function toOpenableUrl(value) {
    const raw = normalizeUrlField(value);
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw)) return raw;
    return `https://${raw}`;
}

/** Website.status / flags: 0 = inativo, 1 = ativo */
function toBoolFlag(value, fallback = false) {
    if (value === undefined || value === null) return fallback;
    return value === 1 || value === true || value === '1';
}

function toApiFlag(value) {
    return value ? 1 : 0;
}

export default {
    name: 'CorporatePreferences',
    mixins: [corporateI18n],
    components: { TitlePage, Loader, CustomFileInput },
    data() {
        return {
            activeTab: 'site',
            loading: true,
            saving: false,
            savingKey: null,
            error: false,
            form: emptyForm(),
            websiteSnapshot: null,
            logoValue: '',
            logoPreview: null,
            logoUploadToken: null,
            domain: emptyDomain(),
            domainError: false,
            domainRefreshing: false,
            domainSaving: false,
            domainVerifying: false,
            hasValidDomain: null,
            dnsCnameTarget: DNS_CNAME_TARGET,
            aRecordIp1: '',
            aRecordIp2: '',
        };
    },
    computed: {
        ...mapState(['token', 'websites', 'corporateCompanySettings']),
        canEdit() {
            return canConfigureCorporate();
        },
        tabs() {
            return [
                { id: 'site', label: this.t.preferences.tabPortal },
                { id: 'collaborator', label: this.t.preferences.tabCollaborator },
                { id: 'banner', label: this.t.preferences.tabBanner },
                { id: 'domain', label: this.t.preferences.tabDomain },
            ];
        },
        domainOwnItems() {
            return [
                {
                    key: 'url1',
                    title: this.t.preferences.domainPrimary,
                    hint: this.t.preferences.domainPrimaryHint,
                },
                {
                    key: 'url2',
                    title: this.t.preferences.domainAdditional,
                    hint: this.t.preferences.domainAdditionalHint,
                },
            ];
        },
        dnsARecords() {
            return [
                {
                    key: 'a1',
                    value: this.aRecordIp1,
                    label: this.t.preferences.domainARecord,
                },
                {
                    key: 'a2',
                    value: this.aRecordIp2,
                    label: this.t.preferences.domainARecord,
                },
            ];
        },
        collaboratorToggleItems() {
            return [
                {
                    key: 'showCertificate',
                    title: this.t.preferences.showCertificate,
                    hint: this.t.preferences.showCertificateHint,
                },
                {
                    key: 'showInquiry',
                    title: this.t.preferences.showInquiry,
                    hint: this.t.preferences.showInquiryHint,
                },
                {
                    key: 'showForum',
                    title: this.t.preferences.showForum,
                    hint: this.t.preferences.showForumHint,
                },
                {
                    key: 'showWarning',
                    title: this.t.preferences.showWarning,
                    hint: this.t.preferences.showWarningHint,
                },
                {
                    key: 'showNotification',
                    title: this.t.preferences.showNotification,
                    hint: this.t.preferences.showNotificationHint,
                },
            ];
        },
        siteUrlItems() {
            return [
                {
                    key: 'url',
                    title: this.t.preferences.url,
                    hint: this.t.preferences.urlHint,
                },
                {
                    key: 'url1',
                    title: this.t.preferences.url1,
                    hint: this.t.preferences.url1Hint,
                },
                {
                    key: 'url2',
                    title: this.t.preferences.url2,
                    hint: this.t.preferences.url2Hint,
                },
            ];
        },
    },
    async created() {
        await this.load();
    },
    beforeUnmount() {
        this.revokeBlob(this.logoPreview);
    },
    methods: {
        ...mapActions(['storeCorporateCompanySettings', 'storeWebsites']),
        revokeBlob(url) {
            if (url && typeof url === 'string' && url.startsWith('blob:')) {
                URL.revokeObjectURL(url);
            }
        },
        syncLocalSettings(website) {
            const current = this.corporateCompanySettings || {};
            const siteEnabled = toBoolFlag(website?.status, true);
            const next = {
                ...current,
                isCorporate: true,
                siteEnabled,
                portal: {
                    ...(current.portal || {}),
                    showCertificate: toBoolFlag(website?.showCertificate, true),
                    showInquiry: toBoolFlag(website?.showInquiry, false),
                    showForum: toBoolFlag(website?.showForum, false),
                    showWarning: Boolean(this.form.showWarning),
                    showNotification: toBoolFlag(website?.showNotification, true),
                },
            };
            this.storeCorporateCompanySettings({ settings: next });
            return next;
        },
        applyMediaFromWebsite(website) {
            const logoUrl = typeof website?.logoFinal === 'string'
                ? website.logoFinal
                : (typeof website?.logo === 'string' && website.logo.startsWith('http')
                    ? website.logo
                    : '');
            this.logoValue = logoUrl;
            this.logoPreview = logoUrl || null;
            this.logoUploadToken = null;
        },
        async load() {
            this.loading = true;
            this.error = false;
            this.loadDomain();
            try {
                await this.loadWebsite();
            } finally {
                this.loading = false;
            }
        },
        async loadWebsite() {
            try {
                const website = await websiteApi.getWebsite(this.token);
                if (!website) {
                    this.error = true;
                    return;
                }

                this.websiteSnapshot = website;
                this.form = {
                    siteEnabled: toBoolFlag(website.status, true),
                    showCertificate: toBoolFlag(website.showCertificate, true),
                    showInquiry: toBoolFlag(website.showInquiry, false),
                    showForum: toBoolFlag(website.showForum, false),
                    showWarning: false,
                    showNotification: toBoolFlag(website.showNotification, true),
                    url: normalizeUrlField(website.url),
                    url1: normalizeUrlField(website.url1),
                    url2: normalizeUrlField(website.url2),
                };
                this.applyMediaFromWebsite(website);

                this.syncLocalSettings(website);
                this.storeWebsites({ websites: website });
            } catch (e) {
                console.error('[corporate] falha ao carregar /website', e);
                this.error = true;
            }
        },
        applyDomainResponse(response) {
            const rawUrl = normalizeUrlField(response?.url);
            let slug = '';
            let url = '';
            if (rawUrl) {
                const host = rawUrl.replace(/^https?:\/\//i, '').split('/')[0];
                slug = host.split('.')[0] || '';
                url = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;
            }
            this.domain = {
                slug,
                url,
                url1: normalizeUrlField(response?.url1),
                url2: normalizeUrlField(response?.url2),
            };
            this.hasValidDomain = null;
            this.aRecordIp1 = normalizeUrlField(response?.aRecordIp1);
            this.aRecordIp2 = normalizeUrlField(response?.aRecordIp2);
        },
        async fetchDomain() {
            const response = await apiSiteDomain.getDomainData(this.token);
            this.applyDomainResponse(response || {});
            this.domainError = false;
        },
        async loadDomain() {
            this.domainRefreshing = true;
            this.domainError = false;
            try {
                await this.fetchDomain();
            } catch (e) {
                console.error('[corporate] falha ao carregar /manager/domain', e);
                this.domainError = true;
            } finally {
                this.domainRefreshing = false;
            }
        },
        onDomainSlugInput() {
            const slug = String(this.domain.slug || '')
                .replace(/[^\w]/gi, '')
                .toLowerCase()
                .slice(0, 30);
            this.domain.slug = slug;
            this.domain.url = slug ? `https://${slug}.weeduc.com.br` : '';
            this.hasValidDomain = null;
        },
        async validateDomain() {
            if (!this.canEdit || this.domainVerifying) return;
            if (!this.domain.url) {
                toast.error(this.t.preferences.domainNameRequired);
                return;
            }
            this.domainVerifying = true;
            try {
                const response = await apiSiteDomain.postVerifyDomaintData(this.token, {
                    url: this.domain.url,
                });
                this.hasValidDomain = response === true;
                if (this.hasValidDomain) {
                    toast.success(this.t.preferences.domainAvailable);
                } else {
                    toast.error(this.t.preferences.domainUnavailable);
                }
            } catch (e) {
                console.error('[corporate] falha ao verificar domínio', e);
                this.hasValidDomain = null;
                toast.error(this.t.preferences.domainVerifyError);
            } finally {
                this.domainVerifying = false;
            }
        },
        async saveDomain() {
            if (!this.canEdit || this.domainSaving) return;
            this.domainSaving = true;
            try {
                await apiSiteDomain.putDomaintData(this.token, {
                    url: this.domain.url,
                    url1: normalizeUrlField(this.domain.url1),
                    url2: normalizeUrlField(this.domain.url2),
                });
                await this.fetchDomain();
                toast.success(this.t.preferences.saved);
            } catch (e) {
                console.error('[corporate] falha ao salvar /manager/domain', e);
                toast.error(this.t.preferences.domainSaveError);
            } finally {
                this.domainSaving = false;
            }
        },
        async copyRaw(value) {
            const text = normalizeUrlField(value);
            if (!text) return;
            try {
                await navigator.clipboard.writeText(text);
                toast.success(this.t.preferences.copied);
            } catch (e) {
                console.error('[corporate] falha ao copiar valor', e);
                toast.error(this.t.common.error);
            }
        },
        async putFileAws(url, file) {
            await apiAWS.putFileAWS(url, file);
        },
        async uploadImage(file) {
            const signature = await apiAWS.getSignatureImage(this.token, file.name);
            await this.putFileAws(signature.url, file);
            return signature.token;
        },
        async onLogoChange(value) {
            if (!this.canEdit) return;
            if (value && typeof value === 'object' && value instanceof File) {
                this.revokeBlob(this.logoPreview);
                this.logoPreview = URL.createObjectURL(value);
                try {
                    const localPreview = this.logoPreview;
                    this.logoUploadToken = await this.uploadImage(value);
                    const updated = await this.persistWebsitePatch({
                        logo: this.logoUploadToken,
                    });
                    this.applyMediaFromWebsite(updated);
                    if (!this.logoPreview && localPreview) {
                        this.logoValue = localPreview;
                        this.logoPreview = localPreview;
                    }
                    toast.success(this.t.preferences.saved);
                } catch (e) {
                    console.error('[corporate] falha no upload do logo', e);
                    toast.error(this.t.common.error);
                }
            } else if (typeof value === 'string') {
                this.logoValue = value;
                this.logoPreview = value || null;
            }
        },
        buildWebsitePayload(extra = {}) {
            return {
                ...(this.websiteSnapshot || {}),
                status: toApiFlag(this.form.siteEnabled),
                showCertificate: toApiFlag(this.form.showCertificate),
                showInquiry: toApiFlag(this.form.showInquiry),
                showForum: toApiFlag(this.form.showForum),
                showNotification: toApiFlag(this.form.showNotification),
                url: normalizeUrlField(this.form.url) || null,
                url1: normalizeUrlField(this.form.url1) || null,
                url2: normalizeUrlField(this.form.url2) || null,
                ...extra,
            };
        },
        canOpenUrl(value) {
            return Boolean(toOpenableUrl(value));
        },
        openUrl(value) {
            const href = toOpenableUrl(value);
            if (!href) return;
            window.open(href, '_blank', 'noopener,noreferrer');
        },
        async copyUrl(value) {
            const href = toOpenableUrl(value);
            if (!href) return;
            try {
                await navigator.clipboard.writeText(href);
                toast.success(this.t.preferences.copied);
            } catch (e) {
                console.error('[corporate] falha ao copiar URL', e);
                toast.error(this.t.common.error);
            }
        },
        applyUrlsFromWebsite(website) {
            this.form = {
                ...this.form,
                url: normalizeUrlField(website?.url),
                url1: normalizeUrlField(website?.url1),
                url2: normalizeUrlField(website?.url2),
            };
        },
        async onUrlBlur(key) {
            if (!this.canEdit || this.saving || this.loading) return;
            const next = normalizeUrlField(this.form[key]);
            const previous = normalizeUrlField(this.websiteSnapshot?.[key]);
            if (next === previous) return;

            this.saving = true;
            this.savingKey = key;
            try {
                await this.persistWebsitePatch();
                this.applyUrlsFromWebsite(this.websiteSnapshot);
                toast.success(this.t.preferences.saved);
            } catch (e) {
                console.error('[corporate] falha ao salvar URL', e);
                this.form = {
                    ...this.form,
                    [key]: previous,
                };
                toast.error(this.t.common.error);
            } finally {
                this.saving = false;
                this.savingKey = null;
            }
        },
        async persistWebsitePatch(extra = {}) {
            if (!this.websiteSnapshot) {
                this.websiteSnapshot = await websiteApi.getWebsite(this.token);
            }
            const websitePayload = this.buildWebsitePayload(extra);
            const updatedWebsite = await websiteApi.editWebsite(
                this.token,
                this.websiteSnapshot?.id,
                websitePayload,
            );
            this.websiteSnapshot = updatedWebsite || websitePayload;
            this.storeWebsites({ websites: this.websiteSnapshot });
            this.syncLocalSettings(this.websiteSnapshot);
            try {
                await websiteApi.refreshCache(this.token);
            } catch (cacheError) {
                console.warn('[corporate] refreshCache falhou (não bloqueia save)', cacheError);
            }
            return this.websiteSnapshot;
        },
        async onToggle(key, value) {
            if (!this.canEdit || this.saving || this.loading) return;

            const previous = this.form[key];
            this.form = {
                ...this.form,
                [key]: Boolean(value),
            };

            if (key === 'showWarning') {
                this.syncLocalSettings(this.websiteSnapshot || {});
                toast.success(this.t.preferences.saved);
                return;
            }

            this.saving = true;
            this.savingKey = key;
            try {
                await this.persistWebsitePatch();
                toast.success(this.t.preferences.saved);
            } catch (e) {
                console.error('[corporate] falha ao salvar configurações', e);
                this.form = {
                    ...this.form,
                    [key]: previous,
                };
                toast.error(this.t.common.error);
            } finally {
                this.saving = false;
                this.savingKey = null;
            }
        },
    },
};
</script>

<style scoped>
@import '@/css/weeduc/admin/corporate.css';

.corporate-preferences-panel {
    text-align: left;
}

.corporate-preferences-lead {
    margin: 0 0 1rem;
    color: #5c6370;
    font-size: 0.875rem;
    line-height: 1.45;
}

.corporate-preferences-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.corporate-preferences-row {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    align-items: center;
    padding: 1rem 1.1rem;
    border: 1px solid #f0f2f5;
    border-radius: 12px;
    background: #fafbfc;
}

.corporate-preferences-row strong {
    display: block;
    color: #1a1d26;
    font-size: 0.95rem;
}

.corporate-preferences-row p {
    margin: 0.25rem 0 0;
    color: #5c6370;
    font-size: 0.85rem;
    line-height: 1.45;
}

.corporate-preferences-upload {
    max-width: 360px;
}

.corporate-preferences-urls {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 0.75rem;
}

.corporate-preferences-url-card {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    min-width: 0;
    padding: 1rem 1.1rem;
    border: 1px solid #f0f2f5;
    border-radius: 12px;
    background: #fafbfc;
}

.corporate-preferences-url-card--main {
    grid-column: 1 / -1;
}

.corporate-preferences-url-card.is-saving {
    opacity: 0.72;
}

.corporate-preferences-url-card__copy strong {
    display: block;
    color: #1a1d26;
    font-size: 0.95rem;
}

.corporate-preferences-url-card__copy p {
    margin: 0.25rem 0 0;
    color: #5c6370;
    font-size: 0.85rem;
    line-height: 1.45;
}

.corporate-preferences-url-card__control {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
}

.corporate-preferences-url-card__input {
    flex: 1;
    min-width: 0;
    height: 42px;
    padding: 0 0.9rem;
    border: 1px solid #e4e7ee;
    border-radius: 10px;
    background: #fff;
    color: #1a1d26;
    font: inherit;
    font-size: 0.9rem;
    outline: none;
}

.corporate-preferences-url-card__input:focus {
    border-color: #160266;
    box-shadow: 0 0 0 3px rgba(22, 2, 102, 0.08);
}

.corporate-preferences-url-card__input:disabled {
    color: #8b90a0;
    background: #f3f4f7;
}

.corporate-preferences-url-card__actions {
    display: flex;
    flex-shrink: 0;
    gap: 0.15rem;
}

.corporate-preferences-url-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: 1px solid #e4e7ee;
    border-radius: 10px;
    background: #fff;
    color: #5c6370;
    cursor: pointer;
}

.corporate-preferences-url-action:hover:not(:disabled) {
    border-color: #d8dbe3;
    background: #f4f5f8;
    color: #160266;
}

.corporate-preferences-url-action:disabled {
    opacity: 0.4;
    cursor: default;
}

.corporate-preferences-banner {
    padding: 0.9rem 1rem;
    margin-bottom: 1rem;
    border-radius: 12px;
    background: #eef0fc;
    color: #160266;
    font-size: 0.875rem;
    font-weight: 600;
}

.corporate-preferences-callout {
    margin: 0 0 1rem;
    padding: 0.75rem 1rem;
    border: 1px solid #fde68a;
    border-radius: 12px;
    background: #fffbeb;
    color: #92400e;
    font-size: 0.8125rem;
    line-height: 1.5;
}

.corporate-preferences-domain-verify {
    flex-shrink: 0;
    height: 42px;
    padding: 0 0.9rem;
    border: 1px solid #160266;
    border-radius: 10px;
    background: #fff;
    color: #160266;
    font: inherit;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
}

.corporate-preferences-domain-verify:hover:not(:disabled) {
    background: #f4f2fb;
}

.corporate-preferences-domain-verify:disabled {
    opacity: 0.45;
    cursor: default;
}

.corporate-preferences-domain-status {
    flex-shrink: 0;
}

.corporate-preferences-domain-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 1.25rem;
}

.corporate-preferences-dns-badge {
    display: inline-flex;
    align-items: center;
    height: 22px;
    margin-bottom: 0.45rem;
    padding: 0 0.5rem;
    border-radius: 999px;
    background: #eef0fc;
    color: #160266;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.04em;
}

.corporate-preferences-url-card__control--stack {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
    align-items: center;
}

.corporate-preferences-domain-retry {
    margin-top: 0.75rem;
    border: 0;
    background: transparent;
    color: #160266;
    font: inherit;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;
}

.corporate-preferences-page .corporate-section-label {
    margin: 1.75rem 0 0.65rem;
}

.corporate-preferences-page .corporate-section-label:first-of-type {
    margin-top: 0;
}

@media (max-width: 720px) {
    .corporate-preferences-row {
        flex-direction: column;
        align-items: flex-start;
    }

    .corporate-preferences-urls {
        grid-template-columns: 1fr;
    }

    .corporate-preferences-url-card__control {
        flex-wrap: wrap;
    }

    .corporate-preferences-url-card__input {
        width: 100%;
        flex: 1 1 100%;
    }
}

.dashboard.dark .corporate-preferences-row,
.dashboard.dark .corporate-preferences-url-card {
    background: #252730;
    border-color: #353945;
}

.dashboard.dark .corporate-preferences-row strong,
.dashboard.dark .corporate-preferences-url-card__copy strong {
    color: #f1f3f7;
}

.dashboard.dark .corporate-preferences-row p,
.dashboard.dark .corporate-preferences-lead,
.dashboard.dark .corporate-preferences-url-card__copy p {
    color: #9ca3b4;
}

.dashboard.dark .corporate-preferences-banner {
    background: rgba(165, 180, 252, 0.12);
    color: #c4b5fd;
}

.dashboard.dark .corporate-section-label {
    color: rgba(255, 255, 255, 0.45);
}

.dashboard.dark .corporate-preferences-url-card__input {
    background: #1f222b;
    border-color: #353945;
    color: #f1f3f7;
}

.dashboard.dark .corporate-preferences-url-card__input:focus {
    border-color: #c4b5fd;
    box-shadow: 0 0 0 3px rgba(165, 180, 252, 0.16);
}

.dashboard.dark .corporate-preferences-url-card__input:disabled {
    background: #2a2e3a;
    color: #9ca3b4;
}

.dashboard.dark .corporate-preferences-url-action {
    background: #1f222b;
    border-color: #353945;
    color: #9ca3b4;
}

.dashboard.dark .corporate-preferences-url-action:hover:not(:disabled) {
    background: rgba(165, 180, 252, 0.12);
    border-color: #454a5a;
    color: #c4b5fd;
}

.dashboard.dark .corporate-preferences-callout {
    background: rgba(251, 191, 36, 0.12);
    border-color: rgba(251, 191, 36, 0.35);
    color: #fcd34d;
}

.dashboard.dark .corporate-preferences-domain-verify {
    background: #1f222b;
    border-color: #c4b5fd;
    color: #c4b5fd;
}

.dashboard.dark .corporate-preferences-domain-verify:hover:not(:disabled) {
    background: rgba(165, 180, 252, 0.12);
}

.dashboard.dark .corporate-preferences-dns-badge,
.dashboard.dark .corporate-preferences-domain-retry {
    background: rgba(165, 180, 252, 0.12);
    color: #c4b5fd;
}

.dashboard.dark .corporate-preferences-domain-retry {
    background: transparent;
}
</style>
